import React, {useState, useMemo} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {updateUser, updateUserPassword} from './_requests'
import Swal from 'sweetalert2'
import {useAuth} from '../../../app/modules/auth'
import {UserModel} from '../../modules/auth/core/_models'
import DatePicker from 'react-multi-date-picker'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'

const Setting: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const {currentUser} = useAuth()
  const [loadingPasswordChange, setLoadingPasswordChange] = useState(false)

  const userDetailsSchema = useMemo(
    () =>
      Yup.object().shape({
        phone: Yup.string().required(intl.formatMessage({id: 'SETTINGS.VALIDATION.PHONE_REQUIRED'})),
        email: Yup.string().required(intl.formatMessage({id: 'SETTINGS.VALIDATION.EMAIL_REQUIRED'})),
        first_name: Yup.string().required(intl.formatMessage({id: 'SETTINGS.VALIDATION.FIRSTNAME_REQUIRED'})),
        last_name: Yup.string().required(intl.formatMessage({id: 'SETTINGS.VALIDATION.LASTNAME_REQUIRED'})),
        national_id: Yup.string()
          .required(intl.formatMessage({id: 'SETTINGS.VALIDATION.NATIONAL_ID_REQUIRED'}))
          .matches(/^\d{10}$/, intl.formatMessage({id: 'SETTINGS.VALIDATION.NATIONAL_ID_10'}))
          .length(10, intl.formatMessage({id: 'SETTINGS.VALIDATION.NATIONAL_ID_10'})),
        birthday: Yup.string()
          .required(intl.formatMessage({id: 'SETTINGS.VALIDATION.BIRTHDAY_REQUIRED'}))
          .matches(/^\d{4}\/\d{2}\/\d{2}$/, intl.formatMessage({id: 'SETTINGS.VALIDATION.BIRTHDAY_FORMAT'})),
        wallet_address: Yup.string()
          .nullable()
          .test(
            'wallet-min-length',
            intl.formatMessage({id: 'SETTINGS.VALIDATION.WALLET_MIN'}),
            (value) => !value || value.length === 0 || value.length >= 26
          )
          .test(
            'wallet-max-length',
            intl.formatMessage({id: 'SETTINGS.VALIDATION.WALLET_MAX'}),
            (value) => !value || value.length <= 100
          ),
        national_card_image: Yup.mixed()
          .nullable()
          .test('fileType', intl.formatMessage({id: 'SETTINGS.VALIDATION.FILE_TYPE'}), (value) => {
            if (!value) return true
            if (typeof value === 'string') {
              if (value.startsWith('data:image/')) {
                const mimeType = value.split(';')[0]?.split(':')[1]
                return mimeType === 'image/jpeg' || mimeType === 'image/png' || mimeType === 'image/jpg'
              }
              return true
            }
            if (value instanceof File) {
              return (
                value.type === 'image/jpg' || value.type === 'image/jpeg' || value.type === 'image/png'
              )
            }
            return false
          })
          .test('fileSize', intl.formatMessage({id: 'SETTINGS.VALIDATION.FILE_SIZE'}), (value) => {
            if (!value) return true
            if (value instanceof File) return value.size <= 5 * 1024 * 1024
            return true
          }),
      }),
    [intl]
  )

  const passwordChangeSchema = useMemo(
    () =>
      Yup.object().shape({
        currentPassword: Yup.string()
          .required(intl.formatMessage({id: 'SETTINGS.VALIDATION.CURRENT_PASSWORD_REQUIRED'}))
          .min(8, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MIN'}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MAX'}))
          .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_PATTERN'})),
        newPassword: Yup.string()
          .required(intl.formatMessage({id: 'SETTINGS.VALIDATION.NEW_PASSWORD_REQUIRED'}))
          .min(8, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MIN'}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MAX'}))
          .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_PATTERN'})),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('newPassword')], intl.formatMessage({id: 'SETTINGS.VALIDATION.PASSWORD_MISMATCH'}))
          .required(intl.formatMessage({id: 'SETTINGS.VALIDATION.CONFIRM_PASSWORD_REQUIRED'})),
      }),
    [intl]
  )

  const formikPasswordChange = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordChangeSchema,
    onSubmit: async (values) => {
      setLoadingPasswordChange(true)
      try {
        await updateUserPassword({
          password: values.newPassword,
          currentPassword: values.currentPassword,
          confirmed: values.confirmPassword,
        })

        await Swal.fire({
          icon: 'success',
          title: intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}),
          text: intl.formatMessage({id: 'SETTINGS.SUCCESS_PASSWORD'}),
          confirmButtonText: intl.formatMessage({id: 'SETTINGS.CONFIRM_BUTTON'}),
        })

        formikPasswordChange.resetForm()
      } catch (error) {
        await Swal.fire({
          icon: 'error',
          title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
          text: intl.formatMessage({id: 'SETTINGS.ERROR_PASSWORD'}),
          confirmButtonText: intl.formatMessage({id: 'SETTINGS.CONFIRM_BUTTON'}),
        })
      } finally {
        setLoadingPasswordChange(false)
      }
    },
  })

  const formik = useFormik<UserModel>({
    initialValues: {
      id: currentUser?.id || 0,
      veryfied: currentUser?.veryfied || 0,
      phone: currentUser?.phone || '',
      email: currentUser?.email || '',
      first_name: currentUser?.first_name || '',
      last_name: currentUser?.last_name || '',
      wallet: currentUser?.wallet || 0,
      plan: currentUser?.plan || null,
      plan_expiry: currentUser?.plan_expiry || null,
      ref_num: currentUser?.ref_num || 0,
      ref_income: currentUser?.ref_income || 0,
      shopUsername: currentUser?.shopUsername || null,
      national_card_image: currentUser?.national_card_image || null,
      national_id: currentUser?.national_id || '',
      birthday: currentUser?.birthday || '',
      wallet_address: currentUser?.wallet_address || '',
    },
    validationSchema: userDetailsSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        // ارسال داده‌ها به سرور
        await updateUser(values)

        await Swal.fire({
          icon: 'success',
          title: intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}),
          text: intl.formatMessage({id: 'SETTINGS.SUCCESS_SAVE'}),
          confirmButtonText: intl.formatMessage({id: 'SETTINGS.CONFIRM_BUTTON'}),
        })
      } catch (error) {
        console.error('Error updating shop:', error)

        const errorResponse = error as {
          response?: {data?: {message?: string; errors?: Record<string, string[]>}}
        }
        let errorMessage = intl.formatMessage({id: 'SETTINGS.ERROR_SAVE'})

        if (errorResponse.response?.data?.message) {
          errorMessage = errorResponse.response.data.message
        }

        if (errorResponse.response?.data?.errors) {
          const errorDetails = Object.values(errorResponse.response.data.errors).flat()
          errorMessage = errorDetails.join(', ')
        }

        await Swal.fire({
          icon: 'error',
          title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
          text: errorMessage,
          confirmButtonText: intl.formatMessage({id: 'SETTINGS.CONFIRM_BUTTON'}),
        })
      } finally {
        setLoading(false)
      }
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => formik.setFieldValue('national_card_image', reader.result)
      reader.readAsDataURL(file)
    }
  }

  return (
    <>
      <div className='card mb-xl-10 p-5'>
        <div id='kt_account_profile_details' className='collapse show'>
          <h3 className='mb-5'>{intl.formatMessage({id: 'SETTINGS.PROFILE_TITLE'})}</h3>

          <form onSubmit={formik.handleSubmit} noValidate className='form'>
            <div className='card-body border-top p-9'>
              {/* First Name */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.FIRSTNAME'})}</label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_FIRSTNAME'})}
                    {...formik.getFieldProps('first_name')}
                  />
                  {formik.touched.first_name && formik.errors.first_name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.first_name}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Last Name */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                  {intl.formatMessage({id: 'AUTH.INPUT.LASTNAME'})}
                </label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_LASTNAME'})}
                    {...formik.getFieldProps('last_name')}
                  />
                  {formik.touched.last_name && formik.errors.last_name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.last_name}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Phone (Non-editable) */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'SETTINGS.LABEL_PHONE'})}</label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_PHONE'})}
                    value={formik.values.phone}
                    readOnly
                  />
                </div>
              </div>

              {/* Email */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}</label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_EMAIL'})}
                    {...formik.getFieldProps('email')}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.email}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* National ID */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'SETTINGS.LABEL_NATIONAL_ID'})}</label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_NATIONAL_ID'})}
                    maxLength={10}
                    {...formik.getFieldProps('national_id')}
                  />
                  {formik.touched.national_id && formik.errors.national_id && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.national_id}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Wallet Address */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>{intl.formatMessage({id: 'SETTINGS.LABEL_WALLET_ADDRESS'})}</label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_WALLET'})}
                    {...formik.getFieldProps('wallet_address')}
                  />
                  {formik.touched.wallet_address && formik.errors.wallet_address && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.wallet_address}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Birthday */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'SETTINGS.LABEL_BIRTHDAY'})}</label>
                <div className='col-lg-8'>
                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={formik.values.birthday || ''}
                    onChange={(date: any) => {
                      const formatted = date?.format?.('YYYY/MM/DD') || ''
                      const toEnglishDigits = (input: string) =>
                        input
                          .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString())
                          .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString())
                      const normalized = toEnglishDigits(formatted)
                      void formik.setFieldValue('birthday', normalized)
                    }}
                    format='YYYY/MM/DD'
                    inputClass='form-control form-control-lg form-control-solid'
                    placeholder={intl.formatMessage({id: 'SETTINGS.PLACEHOLDER_BIRTHDAY'})}
                    editable={false}
                  />
                  {formik.touched.birthday && formik.errors.birthday && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.birthday}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* National Card Image */}
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                  {intl.formatMessage({id: 'SETTINGS.LABEL_NATIONAL_CARD'})}
                </label>
                <small>{intl.formatMessage({id: 'SETTINGS.NATIONAL_CARD_HINT'})}</small>
                <div className='col-lg-8 mt-5'>
                  <div
                    className='image-input image-input-outline shadow-sm'
                    style={{
                      backgroundImage: `url(${
                        formik.values.national_card_image || '/media/avatars/blank.png'
                      })`,
                    }}
                  >
                    <div
                      className='image-input-wrapper w-125px h-125px d-flex align-items-center justify-content-center'
                      style={{
                        cursor: 'pointer',
                        backgroundColor: '#f5f5f5',
                        transition: 'all 0.3s',
                      }}
                      onClick={() => document.getElementById('national_card_image')?.click()}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    >
                      {formik.values.national_card_image ? (
                        <img
                          src={formik.values.national_card_image || '/media/avatars/blank.png'}
                          alt={intl.formatMessage({id: 'SETTINGS.LABEL_NATIONAL_CARD'})}
                          style={{maxWidth: '100%', maxHeight: '100%'}}
                        />
                      ) : (
                        <span className='text-muted'>{intl.formatMessage({id: 'SETTINGS.UPLOAD_NATIONAL_CARD'})}</span>
                      )}
                    </div>
                    <input
                      type='file'
                      id='national_card_image'
                      style={{display: 'none'}}
                      onChange={handleFileChange}
                    />
                  </div>
                  {formik.touched.national_card_image && formik.errors.national_card_image && (
                    <div className='text-danger mt-2'>{formik.errors.national_card_image}</div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className='card-footer d-flex justify-content-end py-6 px-9'>
                <button type='submit' className='btn btn-primary' disabled={loading}>
                  {!loading && intl.formatMessage({id: 'SETTINGS.SAVE_BUTTON'})}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}{' '}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className='card mt-10 p-5'>
        <h3 className='mb-5'>{intl.formatMessage({id: 'SETTINGS.CHANGE_PASSWORD_TITLE'})}</h3>

        <form onSubmit={formikPasswordChange.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            {/* Current Password */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'SETTINGS.CURRENT_PASSWORD'})}</label>
              <div className='col-lg-8'>
                <input
                  type='password'
                  className='form-control form-control-lg form-control-solid'
                  {...formikPasswordChange.getFieldProps('currentPassword')}
                />
                {formikPasswordChange.touched.currentPassword &&
                  formikPasswordChange.errors.currentPassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block text-danger'>
                        {formikPasswordChange.errors.currentPassword}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* New Password */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>{intl.formatMessage({id: 'SETTINGS.NEW_PASSWORD'})}</label>
              <div className='col-lg-8'>
                <input
                  type='password'
                  className='form-control form-control-lg form-control-solid'
                  {...formikPasswordChange.getFieldProps('newPassword')}
                />
                {formikPasswordChange.touched.newPassword &&
                  formikPasswordChange.errors.newPassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block text-danger'>
                        {formikPasswordChange.errors.newPassword}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Confirm Password */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'SETTINGS.CONFIRM_PASSWORD'})}
              </label>
              <div className='col-lg-8'>
                <input
                  type='password'
                  className='form-control form-control-lg form-control-solid'
                  {...formikPasswordChange.getFieldProps('confirmPassword')}
                />
                {formikPasswordChange.touched.confirmPassword &&
                  formikPasswordChange.errors.confirmPassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block text-danger'>
                        {formikPasswordChange.errors.confirmPassword}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Submit Button */}
            <div className='d-flex justify-content-end'>
              <button type='submit' className='btn btn-primary' disabled={loadingPasswordChange}>
                {loadingPasswordChange ? (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  intl.formatMessage({id: 'SETTINGS.CHANGE_PASSWORD_BUTTON'})
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export {Setting}
